import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { LicensePlateField } from "@app.automotus.io/components/forms/VehicleInfoFields/LicensePlateField";
import { StateDropdown } from "@app.automotus.io/components/StateDropdownField";
import { StateAbbreviation } from "@app.automotus.io/components/common/LicensePlateState";
import {
  GET_POLICY_VEHICLE_EXEMPTIONS,
  START_VEHICLE_EXEMPTION,
  StartVehicleExemptionData,
  StartVehicleExemptionVars,
} from "common/graphql";
import { useMutation } from "@apollo/client";
import { useSnackPackContext } from "@app.automotus.io/components/context/SnackPack";
import LoadingButton from "@mui/lab/LoadingButton";

const VEHICLE_DESCRIPTION_OPTIONS = [
  "Government Vehicle",
  "Police Vehicle",
  "City Vehicle",
  "Contractor Vehicle",
  "Other",
] as const;

const OTHER_OPTION = VEHICLE_DESCRIPTION_OPTIONS[VEHICLE_DESCRIPTION_OPTIONS.length - 1];

interface AddVehicleExemptionDialogProps {
  open: boolean;
  onClose: () => void;
}

interface FormValues {
  licensePlate: string;
  licensePlateState: StateAbbreviation | null;
  vehicleDescriptionOption: string;
  vehicleDescriptionOther: string;
}

const validationSchema = yup.object({
  licensePlate: LicensePlateField.validationSchema,
  licensePlateState: yup.mixed<StateAbbreviation>().required("State is required"),
  vehicleDescriptionOption: yup.string().oneOf(["", ...VEHICLE_DESCRIPTION_OPTIONS]),
  vehicleDescriptionOther: yup.string().when("vehicleDescriptionOption", {
    is: OTHER_OPTION,
    then: (schema) =>
      schema.trim().required("Please describe the vehicle").max(255, "Description must be 255 characters or less"),
  }),
});

export const AddVehicleExemptionDialog: React.FC<AddVehicleExemptionDialogProps> = ({ open, onClose }) => {
  const [startVehicleExemption] = useMutation<StartVehicleExemptionData, StartVehicleExemptionVars>(
    START_VEHICLE_EXEMPTION,
  );
  const { publishSnackbarMessage } = useSnackPackContext();

  const initialValues: FormValues = {
    licensePlate: "",
    licensePlateState: null,
    vehicleDescriptionOption: "",
    vehicleDescriptionOther: "",
  };

  const handleSubmit = async (values: FormValues) => {
    const vehicleDescription =
      values.vehicleDescriptionOption === OTHER_OPTION
        ? values.vehicleDescriptionOther.trim() || undefined
        : values.vehicleDescriptionOption || undefined;

    await startVehicleExemption({
      variables: {
        licensePlateNumber: values.licensePlate,
        licensePlateRegion: `us-${values.licensePlateState?.toLowerCase()}`,
        beginAt: new Date(),
        vehicleDescription,
      },
      onCompleted() {
        publishSnackbarMessage({
          message: "Added vehicle exemption",
          severity: "success",
        });
        onClose();
      },
      onError(err) {
        console.error(err);
        publishSnackbarMessage({
          message: "Failed to add vehicle exemption",
          severity: "error",
        });
      },
      refetchQueries: [GET_POLICY_VEHICLE_EXEMPTIONS],
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Vehicle Exemption</DialogTitle>
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {(formik) => (
          <Form>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <LicensePlateField />
                </Grid>
                <Grid item xs={12}>
                  <StateDropdown
                    fullWidth
                    required
                    name="licensePlateState"
                    label="License Plate State"
                    placeholder="Select State"
                    value={formik.values.licensePlateState}
                    onChange={(_, value) => formik.setFieldValue("licensePlateState", value)}
                    onBlur={formik.handleBlur}
                    error={formik.touched.licensePlateState && Boolean(formik.errors.licensePlateState)}
                    helperText={
                      formik.touched.licensePlateState && formik.errors.licensePlateState
                        ? String(formik.errors.licensePlateState)
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    name="vehicleDescriptionOption"
                    label="Vehicle Description"
                    value={formik.values.vehicleDescriptionOption}
                    onChange={(e) => {
                      formik.handleChange(e);
                      if (e.target.value !== OTHER_OPTION) {
                        void formik.setFieldValue("vehicleDescriptionOther", "");
                        void formik.setFieldTouched("vehicleDescriptionOther", false);
                      }
                    }}
                    onBlur={formik.handleBlur}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {VEHICLE_DESCRIPTION_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                {formik.values.vehicleDescriptionOption === OTHER_OPTION && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="vehicleDescriptionOther"
                      label="Describe the vehicle"
                      required
                      value={formik.values.vehicleDescriptionOther}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.vehicleDescriptionOther && Boolean(formik.errors.vehicleDescriptionOther)}
                      helperText={
                        formik.touched.vehicleDescriptionOther && formik.errors.vehicleDescriptionOther
                          ? String(formik.errors.vehicleDescriptionOther)
                          : ""
                      }
                    />
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button fullWidth onClick={onClose}>
                Cancel
              </Button>
              <LoadingButton
                fullWidth
                type="submit"
                variant="contained"
                loading={formik.isSubmitting}
                disabled={formik.isSubmitting}
              >
                Add Exemption
              </LoadingButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default AddVehicleExemptionDialog;
